import { LoaderFunction } from 'react-router-dom';

import {
  ContentfulInstructorLedTraining,
  ContentfulInstructorLedTrainingResponse,
  GET_INSTRUCTOR_LED_TRAINING,
} from 'src/graphql/queries/GetInstructorLedTraining';
import { fetchGraphQL } from 'src/services/contentful/client';
import { getAssetUrl } from 'src/services/http/user';

export interface TrainingPartner {
  id: string;
  companyName: string;
  description: string;
  logoUrl?: string;
  regions: string[];
  website?: string;
  email?: string;
  logoBackgroundUrl?: string;
}

export interface PartnersLoaderProps {
  courseId: string;
  trainingPartners: TrainingPartner[];
  iltData?: ContentfulInstructorLedTraining;
}

interface GetInstructorLedTrainingQueryVariables {
  slug: string;
  preview: boolean;
}

// Mock training partners data
const mockTrainingPartners: TrainingPartner[] = [
  {
    id: '1',
    companyName: 'Glintech',
    description:
      'Wrangle Your Projects and Optimize Atlassian Solutions. Empowering executives to corral chaos and drive innovation through tailored training.',
    regions: ['APAC', 'APAC', 'EMEA'],
    website: 'https://glintech.com',
    email: 'contact@glintech.com',
    logoBackgroundUrl: getAssetUrl('/images/glintech.png'),
  },
  {
    id: '2',
    companyName: 'Catworkx',
    description: 'Integrate the full potential for Enterprise integration based on the Atlassian platform.',
    regions: ['EMEA', 'APAC', 'EMEA'],
    website: 'https://catworkx.com',
    email: 'info@catworkx.com',
    logoBackgroundUrl: getAssetUrl('/images/catworkx.png'),
  },
  {
    id: '3',
    companyName: 'Isos Technology',
    description:
      'Isos Technology helps organizations solve complex business challenges with Atlassian tools and industry best practices to drive results.',
    regions: ['AMER', 'APAC', 'EMEA'],
    website: 'https://isostech.com',
    email: 'contact@isostech.com',
    logoBackgroundUrl: getAssetUrl('/images/isostech.png'),
  },
  {
    id: '4',
    companyName: 'Praecipio',
    description:
      'By fully leveraging the Atlassian platform and a combination of Enterprise Service Management and ITSM best practices, Praecipio helps organizations deliver value.',
    regions: ['AMER', 'APAC', 'EMEA'],
    website: 'https://praecipio.com',
    email: 'info@praecipio.com',
    logoBackgroundUrl: getAssetUrl('/images/praecipio.png'),
  },
  {
    id: '5',
    companyName: 'ServiceRocket',
    description:
      'Since 2003, our Atlassian expertise has enabled thousands of enterprises to reduce complexity and empower users.',
    regions: ['AMER', 'APAC', 'EMEA'],
    website: 'https://servicerocket.com',
    email: 'contact@servicerocket.com',
  },
  {
    id: '6',
    companyName: 'Thinkendo',
    description:
      'At Thinkendo, our Authorized Atlassian Instructors specialize in delivering training to teams of all sizes, all over the world.',
    regions: ['AMER', 'APAC', 'EMEA'],
    website: 'https://thinkendo.com',
    email: 'training@thinkendo.com',
  },
];

const getInstructorLedTrainingData = async (
  slug: string,
  preview: boolean,
): Promise<ContentfulInstructorLedTraining | undefined> => {
  try {
    const response = await fetchGraphQL<
      ContentfulInstructorLedTrainingResponse,
      GetInstructorLedTrainingQueryVariables
    >(GET_INSTRUCTOR_LED_TRAINING, {
      slug,
      preview,
    });

    return response.instructorLedTrainingCollection.items[0];
  } catch (error) {
    return undefined;
  }
};

export const loader: LoaderFunction = async ({ params }): Promise<PartnersLoaderProps> => {
  const courseId = params.courseId ?? '';
  const preview = false; // Can be updated based on environment or query params

  const iltData = await getInstructorLedTrainingData(courseId, preview);

  // For now, return mock data
  return {
    courseId,
    trainingPartners: mockTrainingPartners,
    iltData,
  };
};
